import { useRef, useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon, Loader2, RotateCcw, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";

export const LogoSettings = () => {
  const { logoUrl, custom, refresh } = useSite();
  const { t } = useI18n();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      await refresh();
      toast.success(t(msg));
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("logo", file);
    run(() => api.post("/admin/settings/logo", form), "logoS.updated");
  };

  return (
    <section className="card-carbon rounded-2xl p-6" data-testid="logo-settings">
      <div className="flex items-center gap-2">
        <ImageIcon className="h-5 w-5 text-[var(--gold)]" />
        <h2 className="font-display text-sm font-bold uppercase tracking-wider text-white">{t("logoS.title")}</h2>
      </div>
      <p className="mt-2 text-sm text-neutral-400">{t("logoS.hint")}</p>
      <div className="mt-5 flex flex-wrap items-center gap-5">
        <img src={logoUrl} alt={t("logoS.currentAlt")} className="h-20 w-20 rounded-2xl object-cover ring-1 ring-[rgba(245,197,66,0.5)]" data-testid="logo-settings-preview" />
        <div className="flex flex-wrap gap-2">
          <input ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon" className="hidden" onChange={onFile} data-testid="logo-file-input" />
          <button type="button" className="btn-gold btn-sm" disabled={busy} onClick={() => input.current?.click()} data-testid="logo-upload-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {t("logoS.replace")}
          </button>
          {custom && (
            <button type="button" className="btn-red btn-sm" disabled={busy} onClick={() => run(() => api.delete("/admin/settings/logo"), "logoS.restored")} data-testid="logo-reset-btn">
              <RotateCcw className="h-4 w-4" /> {t("logoS.reset")}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
